import { Copy, Mail } from "lucide-react";
import type { ReactNode } from "react";

import { Section, SectionHead, panel } from "@/features/platform/components/landing-ui";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

const mini = cn(panel, "w-[min(270px,100%)] p-3.5 text-[13px]");

/** Working hours for the drawing in step two: weekdays lit, the weekend off. */
const HOURS = [
  { id: "mon", on: true },
  { id: "tue", on: true },
  { id: "wed", on: true },
  { id: "thu", on: true },
  { id: "fri", on: true },
  { id: "sat", on: false },
];

const steps = (): { id: string; title: string; body: string; art: ReactNode }[] => [
  {
    id: "account",
    title: m.platform_step_account_title(),
    body: m.platform_step_account_body(),
    art: (
      <div className={cn(mini, "grid gap-2")}>
        <span className="flex items-center gap-2 rounded-xl bg-[var(--site-well)] px-3 py-2.5 text-muted-foreground">
          <Mail className="size-4" />
          kasia@studio.pl
        </span>
        <span className="grid h-9 place-items-center rounded-xl bg-foreground text-sm font-medium text-background">
          {m.platform_nav_start()}
        </span>
      </div>
    ),
  },
  {
    id: "hours",
    title: m.platform_step_hours_title(),
    body: m.platform_step_hours_body(),
    art: (
      <div className={mini}>
        <div className="grid grid-cols-6 gap-1">
          {HOURS.map((day) => (
            <span
              key={day.id}
              className={cn(
                "grid h-12 place-items-center rounded-lg text-[10px] font-semibold",
                day.on ? "bg-primary text-primary-foreground" : "bg-[var(--site-well)] text-muted-foreground",
              )}
            >
              {day.on ? "7–20" : "—"}
            </span>
          ))}
        </div>
        <span className="mt-2.5 flex items-center justify-between rounded-xl bg-[var(--site-well)] px-3 py-2">
          {m.platform_step_hours_service()}
          <b className="font-semibold tabular-nums">{m.platform_step_hours_price()}</b>
        </span>
      </div>
    ),
  },
  {
    id: "share",
    title: m.platform_step_share_title(),
    body: m.platform_step_share_body(),
    art: (
      <div className={cn(mini, "flex items-center gap-2.5")}>
        <span className="min-w-0 flex-1 truncate rounded-xl bg-[var(--site-well)] px-3 py-2.5 text-muted-foreground">
          complete-athlete.co/kasia
        </span>
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground">
          <Copy className="size-4" />
        </span>
      </div>
    ),
  },
];

/** Numbered, because the order is the point: an account, the hours, then the link to hand out. */
export const PlatformSteps = () => (
  <Section id="how">
    <SectionHead
      title={
        <>
          {m.platform_steps_title_lead()}
          <br />
          {m.platform_steps_title_tail()}
        </>
      }
      aside={m.platform_steps_aside()}
    />
    <ol className="grid gap-3 lg:grid-cols-3">
      {steps().map((step, index) => (
        <li
          key={step.id}
          className={cn(
            "flex min-h-[300px] flex-col rounded-[28px] p-6 lg:min-h-[360px]",
            index === 1 ? "bg-[var(--site-wash)]" : "bg-[var(--site-tint)]",
          )}
        >
          <span className="text-[13px] text-muted-foreground tabular-nums">{String(index + 1).padStart(2, "0")}</span>
          <div aria-hidden className="grid flex-1 place-items-center py-[18px]">
            {step.art}
          </div>
          <b className="text-3xl font-medium tracking-[-0.03em]">{step.title}</b>
          <p className="mt-1 text-muted-foreground">{step.body}</p>
        </li>
      ))}
    </ol>
  </Section>
);
